import { defineSlotRecipe } from '@chakra-ui/react';

// `createSystem` deep-merges this with the built-in slider recipe, which already supplies the track, thumb
// and `--slider-*-size` vars, so the structural CSS below is redundant and behaviour-neutral to remove.
export const sliderSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'control',
    'track',
    'range',
    'thumb',
    'label',
    'valueText',
    'marker',
    'markerGroup',
    'markerIndicator',
  ],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: 'full',
      gap: '1',
      textStyle: 'sm',
      position: 'relative',
      isolation: 'isolate',
      touchAction: 'none',
    },
    label: {
      fontWeight: 'medium',
      textStyle: 'sm',
    },
    control: {
      justifyContent: 'center',
      alignItems: 'center',
      display: 'inline-flex',
      position: 'relative',
    },
    track: {
      overflow: 'hidden',
      borderRadius: 'full',
      flex: '1',
      bg: 'bg.emphasized',
    },
    range: {
      width: 'inherit',
      height: 'inherit',
      bg: 'colorPalette.solid',
      _disabled: { bg: 'border.emphasized' },
    },
    markerGroup: {
      position: 'absolute!',
      zIndex: '1',
    },
    marker: {
      mt: '1',
      minW: '100px',
      display: 'flex',
      alignItems: 'center',
      gap: 'calc(var(--slider-thumb-size) / 2)',
      color: 'fg.muted',
      textStyle: 'xs',
    },
    markerIndicator: {
      width: 'var(--slider-marker-size)',
      height: 'var(--slider-marker-size)',
      borderRadius: 'full',
      bg: 'bg',
    },
    thumb: {
      width: 'var(--slider-thumb-size)',
      height: 'var(--slider-thumb-size)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      outline: 0,
      zIndex: '2',
      borderRadius: 'full',
      bg: 'colorPalette.solid',
      transition: 'shadow',
      _focusVisible: {
        ring: '3px',
        ringColor: 'colorPalette.focusRing/50',
      },
      _disabled: { bg: 'border.emphasized' },
    },
  },
  variants: {
    size: {
      sm: {
        root: {
          '--slider-thumb-size': 'sizes.4',
          '--slider-track-size': 'sizes.1.5',
          '--slider-marker-center': '6px',
          '--slider-marker-size': 'sizes.1',
          '--slider-marker-inset': '3px',
        },
      },
      md: {
        root: {
          '--slider-thumb-size': 'sizes.5',
          '--slider-track-size': 'sizes.2',
          '--slider-marker-center': '8px',
          '--slider-marker-size': 'sizes.1',
          '--slider-marker-inset': '4px',
        },
      },
      lg: {
        root: {
          '--slider-thumb-size': 'sizes.6',
          '--slider-track-size': 'sizes.2.5',
          '--slider-marker-center': '9px',
          '--slider-marker-size': 'sizes.1.5',
          '--slider-marker-inset': '5px',
        },
      },
    },
    orientation: {
      vertical: {
        root: {
          display: 'inline-flex',
          width: 'auto',
        },
        control: {
          flexDirection: 'column',
          height: '100%',
          minWidth: 'var(--slider-thumb-size)',
        },
        track: {
          width: 'var(--slider-track-size)',
        },
        thumb: {
          left: '50%',
          translate: '-50% 0',
        },
        markerGroup: {
          insetStart: 'var(--slider-marker-center)',
          insetBlock: 'var(--slider-marker-inset)',
        },
        marker: {
          fontSize: 'sm',
          ml: '2.5',
          mb: '-2',
          flexDirection: 'row',
        },
      },
      horizontal: {
        control: {
          flexDirection: 'row',
          width: '100%',
          minHeight: 'var(--slider-thumb-size)',
        },
        track: {
          height: 'var(--slider-track-size)',
        },
        thumb: {
          top: '50%',
          translate: '0 -50%',
        },
        markerGroup: {
          top: 'var(--slider-marker-center)',
          insetInline: 'var(--slider-marker-inset)',
        },
        marker: {
          fontSize: 'sm',
          ml: '-2.5',
          flexDirection: 'column',
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
    orientation: 'horizontal',
  },
});
